import React, { useEffect, useCallback, useState } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { socket } from "../../../common/socket";

// Redux actions
import { fetchSettingsData, updateSettingsAction, declareManualWinner } from "../../../slices/ro/settings/thunk";
import { updateGameState, updateBidPredictions, setManualWinnerWindow, resetRound, setSelectedWinner } from "../../../slices/ro/settings/reducer";

import { toast } from "react-toastify";

import "./Dashboard.css";
import { dashboardCache } from "../../../helpers/dashboardCache";
import { updateHistory } from "../../../slices/ro/settings/reducer";
import redDiamondImg from "../../../assets/images/red.png";
import blackDiamondImg from "../../../assets/images/black.png";

import {
    RO_RED_NUMBERS as RED_NUMBERS,
    RO_BLACK_NUMBERS as BLACK_NUMBERS,
    RO_MAPPINGS,
    PHASE_COLORS,
    MULTIPLIER_OPTIONS,
    SITE_TITLE,
    GAME_MODES,
    CACHE_KEYS,
    RETRY_INTERVAL,
    SOCKET_EVENTS
} from "../../../common/constants";

const Dashboard = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [customMultiplier, setCustomMultiplier] = useState("");
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [lastTenWinners, setLastTenWinners] = useState(() => dashboardCache.get(CACHE_KEYS.RO) || []);
    const [currentRoundWinner, setCurrentRoundWinner] = useState(null);
    const [isSocketConnected, setIsSocketConnected] = useState(socket.connected);
    const [hoveredWinner, setHoveredWinner] = useState(null);
    const [onlineCount, setOnlineCount] = useState(0);

    const {
        settings,
        timerDisplay,
        phase,
        roundNumber,
        bidDetails,
        totalAmount,
        isManualWinnerWindowOpen,
        selectedWinner,
        loading,
        globalReturnMultiplier,
        lastTenWinners: reduxHistory // Select from Redux
    } = useSelector((state) => state.ro.settings || {});

    // Sync local state with Redux on load
    useEffect(() => {
        if (reduxHistory && reduxHistory.length > 0) {
            setLastTenWinners(reduxHistory);
        }
    }, [reduxHistory]);

    // Fetch settings on mount
    useEffect(() => {
        dispatch(fetchSettingsData());
    }, [dispatch]);

    // Unified Socket Lifecycle Hook
    useEffect(() => {
        const requestHistory = () => {
            socket.emit(SOCKET_EVENTS.RO.HISTORY);
            socket.emit(SOCKET_EVENTS.RO.HISTORY_FALLBACK); // Singular fallback
            socket.emit("getGameOnlineStats", { gameType: "ro" });
        };

        const handleTimerUpdate = (data) => {
            dispatch(updateGameState({
                timeLeft: data.timeLeft || data.timer || 0,
                phase: data.phase,
                roundNumber: data.roundNumber,
                status: data.status
            }));
        };

        const handlePhaseChange = (data) => {
            dispatch(updateGameState({
                phase: data.phase,
                timeLeft: data.timeLeft || data.timer
            }));
        };

        const handleNewRound = (data) => {
            dispatch(resetRound());
            setCurrentRoundWinner(null);
            if (data && data.lastTenWinners && Array.isArray(data.lastTenWinners)) {
                dashboardCache.set(CACHE_KEYS.RO, data.lastTenWinners);
                setLastTenWinners(data.lastTenWinners);
                dispatch(updateHistory(data.lastTenWinners)); // Sync Redux
            } else {
                requestHistory(); // Re-request on new round if not provided
            }
        };

        const handlePlaceBid = (data) => {
            const bidData = data.data || data;
            if (!bidData) return;

            const numberTotals = {};
            const bids = {};
            let totalAmountToDispatch = 0;

            // Normalize shorthand / alternate bet values to canonical board keys
            const normalizeBetValue = (betType, val) => {
                const v = String(val);
                if (betType === "DOZEN") {
                    if (v === "1" || v === "1st 12") return "1_12";
                    if (v === "2" || v === "2nd 12") return "13_24";
                    if (v === "3" || v === "3rd 12") return "25_36";
                }
                if (betType === "LOW_HIGH") {
                    if (v === "1-18") return "LOW";
                    if (v === "19-36") return "HIGH";
                }
                return v; // Already canonical (e.g. COL_1, RED, ODD, EVEN…)
            };

            const processSingleBet = (bet) => {
                const { betType, betValues, betValue, amount, number, point } = bet;
                // Handle both singular betValue (array or string) and plural betValues
                let actualValues = [];
                if (betValues && Array.isArray(betValues)) actualValues = betValues;
                else if (betValue) actualValues = Array.isArray(betValue) ? betValue : [betValue];
                else if (number !== undefined) actualValues = [number];

                const actualAmount = amount !== undefined ? amount : (point !== undefined ? point : 0);

                if (actualValues.length === 0) return;

                const insideBetTypes = ["SPLIT", "STREET", "CORNER", "SIX_LINE", "FIVE_NUMBER"];

                if (insideBetTypes.includes(betType)) {
                    // Grouped Key for Inside Bets - Sort values to ensure uniqueness
                    const key = `${betType}_${actualValues.map(v => String(v)).sort((a, b) => Number(a) - Number(b)).join('_')}`;
                    numberTotals[key] = (numberTotals[key] || 0) + actualAmount;
                    bids[key] = (bids[key] || 0) + 1;
                } else if (["DOZEN", "LOW_HIGH", "ODD_EVEN", "COLOR", "COLUMN"].includes(betType)) {
                    // Outside bets use specific normalized keys
                    const firstVal = actualValues[0];
                    const key = normalizeBetValue(betType, firstVal);
                    numberTotals[key] = (numberTotals[key] || 0) + actualAmount;
                    bids[key] = (bids[key] || 0) + 1;
                } else {
                    // Single number bets
                    const key = String(actualValues[0]);
                    numberTotals[key] = (numberTotals[key] || 0) + actualAmount;
                    bids[key] = (bids[key] || 0) + 1;
                }
            };

            if (Array.isArray(bidData.bets)) {
                totalAmountToDispatch = bidData.totalBetAmount || 0;
                bidData.bets.forEach(processSingleBet);
            } else if (bidData.number !== undefined || bidData.betValues || bidData.betType) {
                // Single bet update
                totalAmountToDispatch = bidData.totalAmount || bidData.amount || bidData.point || 0;
                processSingleBet(bidData);
            } else {
                return;
            }

            dispatch(updateBidPredictions({
                bids,
                totalAmount: totalAmountToDispatch,
                numberTotals,
                isIncremental: true
            }));
        };

        const handleLastTenWinners = (data) => {
            let winnersArray = [];
            if (Array.isArray(data)) winnersArray = data;
            else if (data && data.lastTenWinners) winnersArray = data.lastTenWinners;
            else if (data && data.data) {
                if (Array.isArray(data.data)) winnersArray = data.data;
                else if (data.data.lastTenWinners) winnersArray = data.data.lastTenWinners;
            }

            if (winnersArray && winnersArray.length > 0) {
                dashboardCache.set(CACHE_KEYS.RO, winnersArray);
                setLastTenWinners(winnersArray);
                dispatch(updateHistory(winnersArray)); // Sync Redux
            }
        };

        const handleManualWinnerConfirmed = (data) => {
            const winner = data.winningNumber !== undefined ? data.winningNumber : data;
            setCurrentRoundWinner(winner);
            dispatch(setManualWinnerWindow(false)); // Lock window once confirmed
        };

        const handleManualWinnerWindow = (data) => {
            dispatch(setManualWinnerWindow(data.isOpen !== undefined ? data.isOpen : true));
        };

        const handlePlaySpin = (data) => {
            if (data && data.winningNumber !== undefined) {
                setCurrentRoundWinner(data.winningNumber);
            }
            dispatch(setManualWinnerWindow(false)); // Lock window when spin starts
        };

        const handleConnect = () => {
            setIsSocketConnected(true);
            requestHistory();
        };

        const handleDisconnect = () => {
            setIsSocketConnected(false);
        };

        const handleOnlineStats = (data) => {
            if (data && data.gameType === "ro") {
                setOnlineCount(data.onlineUsersCount);
            }
        };

        // 1. Register Listeners FIRST
        socket.on(SOCKET_EVENTS.RO.TIMER, handleTimerUpdate);
        socket.on(SOCKET_EVENTS.RO.PHASE, handlePhaseChange);
        socket.on(SOCKET_EVENTS.RO.NEW_ROUND, handleNewRound);
        socket.on(SOCKET_EVENTS.RO.PLACE_BID, handlePlaceBid);
        socket.on(SOCKET_EVENTS.RO.HISTORY, handleLastTenWinners);
        socket.on(SOCKET_EVENTS.RO.HISTORY_FALLBACK, handleLastTenWinners); // Singular fallback
        socket.on(SOCKET_EVENTS.RO.MANUAL_CONFIRMED, handleManualWinnerConfirmed);
        socket.on(SOCKET_EVENTS.RO.MANUAL_WINDOW, handleManualWinnerWindow);
        socket.on(SOCKET_EVENTS.RO.PLAY_SPIN, handlePlaySpin);
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("admin-onlineStatsResponse", handleOnlineStats);

        // 2. Initial State & Request
        setIsSocketConnected(socket.connected);
        requestHistory();

        // 3. Retry mechanism if still empty
        const retryTimer = setInterval(() => {
            if (lastTenWinners.length === 0 && socket.connected) {
                requestHistory();
            }
        }, RETRY_INTERVAL);

        return () => {
            clearInterval(retryTimer);
            socket.off(SOCKET_EVENTS.RO.TIMER, handleTimerUpdate);
            socket.off(SOCKET_EVENTS.RO.PHASE, handlePhaseChange);
            socket.off(SOCKET_EVENTS.RO.NEW_ROUND, handleNewRound);
            socket.off(SOCKET_EVENTS.RO.PLACE_BID, handlePlaceBid);
            socket.off(SOCKET_EVENTS.RO.HISTORY, handleLastTenWinners);
            socket.off(SOCKET_EVENTS.RO.HISTORY_FALLBACK, handleLastTenWinners);
            socket.off(SOCKET_EVENTS.RO.MANUAL_CONFIRMED, handleManualWinnerConfirmed);
            socket.off(SOCKET_EVENTS.RO.MANUAL_WINDOW, handleManualWinnerWindow);
            socket.off(SOCKET_EVENTS.RO.PLAY_SPIN, handlePlaySpin);
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("admin-onlineStatsResponse", handleOnlineStats);
        };
    }, [dispatch, lastTenWinners.length]); // length ensures retry stops when data arrives

    // Handle jackpot multiplier change
    const handleMultiplierChange = useCallback(async (multiplier) => {
        const result = await dispatch(updateSettingsAction({
            globalReturnMultiplier: multiplier,
            manualWinnerEnabled: true,
            gameMode: settings.gameMode || GAME_MODES.MEDIUM
        }));

        if (result && result.success) {
            toast.success("Settings updated successfully");
        } else if (result && result.error) {
            toast.error(result.error);
        }
    }, [dispatch, settings]);

    // Handle custom multiplier submit
    const handleCustomSubmit = () => {
        const value = parseInt(customMultiplier, 10);
        if (value && value > 0) {
            handleMultiplierChange(value);
            setShowCustomModal(false);
            setCustomMultiplier("");
        }
    };

    // Handle manual winner selection
    const handleManualWinner = useCallback((number) => {
        if (!isManualWinnerWindowOpen) return;
        dispatch(setSelectedWinner(number));
        dispatch(declareManualWinner(number));
    }, [dispatch, isManualWinnerWindowOpen]);

    document.title = `Roulette Dashboard | ${SITE_TITLE}`;

    const multiplierOptions = MULTIPLIER_OPTIONS;
    const currentMultiplier = globalReturnMultiplier ? Number(globalReturnMultiplier) : null;
    const isCustomMultiplier = currentMultiplier && !multiplierOptions.includes(currentMultiplier);

    const getPhaseColor = () => {
        return PHASE_COLORS[phase] || PHASE_COLORS.default;
    };

    // Calculate payout and admin P&L for a selected/hovered winning number (roulette)
    const calculateRoulettePayoutAndPL = (winNum) => {
        if (winNum === null || winNum === undefined) return null;
        const winStr = String(winNum);
        const winNumInt = Number(winNum);
        let totalPayout = 0;
        let totalCollected = 0;

        // Iterate all bidDetails to calculate total collected and payout
        Object.entries(bidDetails).forEach(([key, detail]) => {
            const amount = Number(detail.totalAmount) || 0;
            totalCollected += amount;

            // Check if this bet key wins for the given number
            let multiplier = 0;

            // Straight number bet (0, 00, 1-36)
            if (key === winStr) {
                multiplier = 36; // pays 35:1 + original = 36x
            }
            // Inside bets
            else if (key.startsWith('SPLIT_')) {
                const nums = key.replace('SPLIT_', '').split('_');
                if (nums.includes(winStr)) multiplier = 18; // 17:1 + 1
            }
            else if (key.startsWith('STREET_')) {
                const nums = key.replace('STREET_', '').split('_');
                if (nums.includes(winStr)) multiplier = 12; // 11:1 + 1
            }
            else if (key.startsWith('CORNER_')) {
                const nums = key.replace('CORNER_', '').split('_');
                if (nums.includes(winStr)) multiplier = 9; // 8:1 + 1
            }
            else if (key.startsWith('SIX_LINE_') || key.startsWith('FIVE_NUMBER_')) {
                const prefix = key.startsWith('SIX_LINE_') ? 'SIX_LINE_' : 'FIVE_NUMBER_';
                const nums = key.replace(prefix, '').split('_');
                if (nums.includes(winStr)) multiplier = 7; // 6:1 + 1 (5:1 for five number)
            }
            // Outside bets - only win on 1-36, not 0/00
            else if (winStr !== '0' && winStr !== '00') {
                if (key === 'RED' && RED_NUMBERS.includes(winNumInt)) multiplier = 2; // 1:1 + 1
                else if (key === 'BLACK' && BLACK_NUMBERS.includes(winNumInt)) multiplier = 2;
                else if (key === 'ODD' && winNumInt % 2 !== 0) multiplier = 2;
                else if (key === 'EVEN' && winNumInt % 2 === 0) multiplier = 2;
                else if (key === 'LOW' && winNumInt >= 1 && winNumInt <= 18) multiplier = 2;
                else if (key === 'HIGH' && winNumInt >= 19 && winNumInt <= 36) multiplier = 2;
                else if (key === '1_12' && winNumInt >= 1 && winNumInt <= 12) multiplier = 3; // 2:1 + 1
                else if (key === '13_24' && winNumInt >= 13 && winNumInt <= 24) multiplier = 3;
                else if (key === '25_36' && winNumInt >= 25 && winNumInt <= 36) multiplier = 3;
                else if (key === 'COL_1' && RO_MAPPINGS.COL_1.includes(winNumInt)) multiplier = 3;
                else if (key === 'COL_2' && RO_MAPPINGS.COL_2.includes(winNumInt)) multiplier = 3;
                else if (key === 'COL_3' && RO_MAPPINGS.COL_3.includes(winNumInt)) multiplier = 3;
            }

            if (multiplier > 0) {
                totalPayout += amount * multiplier;
            }
        });

        const adminPL = totalCollected - totalPayout;
        return { payout: totalPayout, adminPL, totalCollected };
    };

    // Get the number to show calculations for (hovered takes priority, then selected)
    const activeManualNumber = hoveredWinner !== null ? hoveredWinner : selectedWinner;
    const manualCalc = calculateRoulettePayoutAndPL(activeManualNumber);

    // American Roulette Constants


    const getNumberColor = (num) => {
        if (num === "0" || num === "00") return "green";
        if (RED_NUMBERS.includes(Number(num))) return "red";
        if (BLACK_NUMBERS.includes(Number(num))) return "black";
        return "green"; // Default
    };

    const renderCellContent = (num, displayLabel = null, isManual, isBottomRow = false) => {
        let amount = 0;
        let bidCount = 0;

        // Determine if it's a standard number (0, 00, 1-36)
        const isStandardNumber = !isNaN(Number(num)) || num === "0" || num === "00";

        // Always show raw bid totals from bidDetails
        const details = bidDetails[String(num)] || { count: 0, totalAmount: 0 };
        bidCount = details.count || 0;
        amount = details.totalAmount || 0;

        const hasBids = amount > 0;
        const finalValue = displayLabel || (isStandardNumber ? num : null);

        const formatAmount = (val) => {
            if (typeof val !== 'number') return val;
            if (val === 0) return "0";
            return Number(val % 1 !== 0 ? val.toFixed(2) : val).toLocaleString();
        };

        const content = (
            <div className={`cell-info ${hasBids ? 'has-bids' : ''} ${isBottomRow ? 'bottom-row-content' : ''}`}>
                {finalValue && (
                    <span className={isStandardNumber ? "cell-number" : "cell-label"}>
                        {finalValue}
                    </span>
                )}
                <div className="bid-point-wrapper">
                    {isBottomRow ? (
                        <div className="horizontal-bid-point">
                            <span className="cell-bid">bid: {bidCount}</span>
                            <span className="separator">|</span>
                            <span className="cell-point">point: {formatAmount(amount)}</span>
                        </div>
                    ) : (
                        <>
                            <span className="cell-bid">bid: {bidCount}</span>
                            <span className="cell-point">point: {formatAmount(amount)}</span>
                        </>
                    )}
                </div>
            </div>
        );

        if (isManual && isStandardNumber) {
            const isSelected = selectedWinner?.toString() === num?.toString();
            const isHovered = hoveredWinner?.toString() === num?.toString();
            return (
                <button
                    className={`manual-winner-cell-btn ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                    onClick={() => handleManualWinner(num)}
                    onMouseEnter={() => setHoveredWinner(num)}
                    onMouseLeave={() => setHoveredWinner(null)}
                    disabled={!isManualWinnerWindowOpen || loading}
                    title={!isManualWinnerWindowOpen ? "Manual winner window is closed" : `Select ${num} as winner`}
                >
                    {content}
                </button>
            );
        }

        return content;
    };

    const formatAmount = (val) => {
        if (typeof val !== 'number') return val;
        if (val === 0) return "0";
        return Number(val % 1 !== 0 ? val.toFixed(2) : val).toLocaleString();
    };

    const getInsideBetCoords = (type, vals) => {
        const rowMap = {
            '3': 1, '6': 1, '9': 1, '12': 1, '15': 1, '18': 1, '21': 1, '24': 1, '27': 1, '30': 1, '33': 1, '36': 1,
            '2': 3, '5': 3, '8': 3, '11': 3, '14': 3, '17': 3, '20': 3, '23': 3, '26': 3, '29': 3, '32': 3, '35': 3,
            '1': 5, '4': 5, '7': 5, '10': 5, '13': 5, '16': 5, '19': 5, '22': 5, '25': 5, '28': 5, '31': 5, '34': 5,
            '00': 1, '0': 4
        };

        const getCol = (val) => {
            if (val === '0' || val === '00') return 1;
            return Math.floor((Number(val) - 1) / 3) + 2;
        };

        const getRowRange = (val) => {
            if (val === '00') return { start: 1, end: 4 };
            if (val === '0') return { start: 4, end: 7 };
            // Standard numbers logic from renderRouletteTable
            // Row 3 (3,6,9..): R1-3, Row 2 (2,5,8..): R3-5, Row 1 (1,4,7..): R5-7
            if (["3", "6", "9", "12", "15", "18", "21", "24", "27", "30", "33", "36"].includes(String(val))) return { start: 1, end: 3 };
            if (["2", "5", "8", "11", "14", "17", "20", "23", "26", "29", "32", "35"].includes(String(val))) return { start: 3, end: 5 };
            if (["1", "4", "7", "10", "13", "16", "19", "22", "25", "28", "31", "34"].includes(String(val))) return { start: 5, end: 7 };
            return { start: 1, end: 3 };
        };

        try {
            if (type === 'SPLIT' && vals.length === 2) {
                const c1 = getCol(vals[0]), c2 = getCol(vals[1]);
                const s1 = getRowRange(vals[0]), s2 = getRowRange(vals[1]);

                const minC = Math.min(c1, c2);
                const maxC = Math.max(c1, c2);

                if (c1 === c2) { // Vertical split (Top-to-Bottom) - Use Union
                    const minR = Math.min(s1.start, s2.start);
                    const maxR = Math.max(s1.end, s2.end);
                    return { gridArea: `${minR} / ${c1} / ${maxR} / ${c1 + 1}` };
                } else { // Horizontal split (Side-by-Side) - Use Overlap
                    const overlapStart = Math.max(s1.start, s2.start);
                    const overlapEnd = Math.min(s1.end, s2.end);
                    return { gridArea: `${overlapStart} / ${minC} / ${overlapEnd} / ${maxC + 1}` };
                }
            }

            if (type === 'CORNER' && vals.length === 4) {
                const rs = vals.map(v => rowMap[v]);
                const cs = vals.map(v => getCol(v));
                return { gridArea: `${Math.min(...rs)} / ${Math.min(...cs)} / ${Math.max(...rs) + 2} / ${Math.max(...cs) + 1}` };
            }

            if (type === 'STREET' && vals.length === 3) {
                const rs = vals.map(v => rowMap[v]);
                const cs = vals.map(v => getCol(v));
                const minR = Math.min(...rs);
                const maxR = Math.max(...rs);
                const minC = Math.min(...cs);
                const maxC = Math.max(...cs);

                // If street covers multiple columns (special Zero streets), center it on the corner
                if (minC !== maxC) {
                    return { gridArea: `${minR} / ${minC} / ${maxR + 2} / ${maxC + 1}` };
                }

                // Standard column street - align to top
                return { gridArea: `${minR} / ${minC} / 7 / ${maxC + 1}`, alignTop: true };
            }

            if (type === 'SIX_LINE' && vals.length === 6) {
                // Between two streets, top boundary
                const cs = vals.map(v => getCol(v));
                const minC = Math.min(...cs);
                const maxC = Math.max(...cs);
                return { gridArea: `1 / ${minC} / 7 / ${maxC + 1}`, alignTop: true };
            }
        } catch (e) {
            return null;
        }
        return null;
    };

    const renderInsideBetMarkers = () => {
        return Object.keys(bidDetails).filter(key =>
            key.startsWith('SPLIT_') || key.startsWith('CORNER_') ||
            key.startsWith('STREET_') || key.startsWith('SIX_LINE_') ||
            key.startsWith('FIVE_NUMBER_')
        ).map(key => {
            const parts = key.split('_');
            const type = parts[0];
            const vals = parts.slice(1);
            const coords = getInsideBetCoords(type, vals);

            if (!coords) return null;

            const detail = bidDetails[key];
            return (
                <div key={key} className="inside-bet-marker" style={{ gridArea: coords.gridArea, alignItems: coords.alignTop ? 'flex-start' : 'center' }}>
                    <div className="marker-badge">
                        <span className="marker-bid">{detail.count}</span>
                        <span className="marker-point">{formatAmount(detail.totalAmount)}</span>
                    </div>
                </div>
            );
        });
    };

    const renderRouletteTable = (isManual = false) => {
        const row1 = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36];
        const row2 = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35];
        const row3 = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34];

        return (
            <div className="roulette-container">
                <div className={`roulette-grid ${isManual ? 'manual-mode' : ''}`}>
                    {/* 00 and 0 Column - Wrapper to manage internal borders/spacing */}
                    <div className="zero-column" style={{ gridArea: '1 / 1 / 7 / 2' }}>
                        <div className={`roulette-cell green zero-top ${isManual && selectedWinner?.toString() === '00' ? 'selected' : ''}`}>
                            <svg className="cell-border-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <polygon points="0,50 25,0 100,0 100,100 25,100"
                                    fill="none"
                                    stroke={isManual && selectedWinner?.toString() === '00' ? '#FFD700' : 'white'}
                                    strokeWidth={isManual && selectedWinner?.toString() === '00' ? '8' : '4'}
                                    vectorEffect="non-scaling-stroke"
                                />
                            </svg>
                            {renderCellContent('00', null, isManual)}
                        </div>
                        <div className={`roulette-cell green zero-bottom ${isManual && selectedWinner?.toString() === '0' ? 'selected' : ''}`}>
                            <svg className="cell-border-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <polygon points="0,50 25,0 100,0 100,100 25,100"
                                    fill="none"
                                    stroke={isManual && selectedWinner?.toString() === '0' ? '#FFD700' : 'white'}
                                    strokeWidth={isManual && selectedWinner?.toString() === '0' ? '8' : '4'}
                                    vectorEffect="non-scaling-stroke"
                                />
                            </svg>
                            {renderCellContent('0', null, isManual)}
                        </div>
                    </div>

                    {/* Row 1 Numbers */}
                    {row1.map((num, i) => (
                        <div key={num} className={`roulette-cell ${getNumberColor(num)}`} style={{ gridArea: `1 / ${i + 2} / 3 / ${i + 3}` }}>
                            {renderCellContent(num, null, isManual)}
                        </div>
                    ))}

                    {/* Row 2 Numbers */}
                    {row2.map((num, i) => (
                        <div key={num} className={`roulette-cell ${getNumberColor(num)}`} style={{ gridArea: `3 / ${i + 2} / 5 / ${i + 3}` }}>
                            {renderCellContent(num, null, isManual)}
                        </div>
                    ))}

                    {/* Row 3 Numbers */}
                    {row3.map((num, i) => (
                        <div key={num} className={`roulette-cell ${getNumberColor(num)}`} style={{ gridArea: `5 / ${i + 2} / 7 / ${i + 3}` }}>
                            {renderCellContent(num, null, isManual)}
                        </div>
                    ))}

                    {/* Column Cells */}
                    <div className="roulette-cell col-cell" style={{ gridArea: '1 / 14 / 3 / 15' }}>
                        {renderCellContent('COL_3', '2 to 1', isManual)}
                    </div>
                    <div className="roulette-cell col-cell" style={{ gridArea: '3 / 14 / 5 / 15' }}>
                        {renderCellContent('COL_2', '2 to 1', isManual)}
                    </div>
                    <div className="roulette-cell col-cell" style={{ gridArea: '5 / 14 / 7 / 15' }}>
                        {renderCellContent('COL_1', '2 to 1', isManual)}
                    </div>

                    {/* Dozens Row */}
                    <div className="roulette-cell dozen-cell" style={{ gridArea: '7 / 2 / 8 / 6' }}>
                        {renderCellContent('1_12', '1st 12', isManual, true)}
                    </div>
                    <div className="roulette-cell dozen-cell" style={{ gridArea: '7 / 6 / 8 / 10' }}>
                        {renderCellContent('13_24', '2nd 12', isManual, true)}
                    </div>
                    <div className="roulette-cell dozen-cell" style={{ gridArea: '7 / 10 / 8 / 14' }}>
                        {renderCellContent('25_36', '3rd 12', isManual, true)}
                    </div>

                    {/* Outside Bets Row */}
                    <div className="roulette-cell extra-bet-cell" style={{ gridArea: '8 / 2 / 9 / 4' }}>
                        {renderCellContent('LOW', '1 TO 18', isManual, true)}
                    </div>
                    <div className="roulette-cell extra-bet-cell" style={{ gridArea: '8 / 4 / 9 / 6' }}>
                        {renderCellContent('EVEN', 'EVEN', isManual, true)}
                    </div>
                    <div className="roulette-cell extra-bet-cell red-diamond" style={{ gridArea: '8 / 6 / 9 / 8' }}>
                        <img src={redDiamondImg} alt="red" className="diamond-img" />
                        {renderCellContent('RED', null, isManual, true)}
                    </div>
                    <div className="roulette-cell extra-bet-cell black-diamond" style={{ gridArea: '8 / 8 / 9 / 10' }}>
                        <img src={blackDiamondImg} alt="black" className="diamond-img" />
                        {renderCellContent('BLACK', null, isManual, true)}
                    </div>
                    <div className="roulette-cell extra-bet-cell" style={{ gridArea: '8 / 10 / 9 / 12' }}>
                        {renderCellContent('ODD', 'ODD', isManual, true)}
                    </div>
                    <div className="roulette-cell extra-bet-cell" style={{ gridArea: '8 / 12 / 9 / 14' }}>
                        {renderCellContent('HIGH', '19 TO 36', isManual, true)}
                    </div>

                    {/* Inside Bet Markers Overlay */}
                    {renderInsideBetMarkers()}

                    {/* Corner Spacers */}
                    <div className="roulette-cell empty-cell" style={{ gridArea: '7 / 1 / 9 / 2', borderRight: '2px solid #fff' }}></div>
                    <div className="roulette-cell empty-cell" style={{ gridArea: '7 / 14 / 9 / 15' }}></div>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content" style={{ marginBottom: '35px' }}>
                <Container fluid>
                    {/* Header with Timer */}
                    <div className="dashboard-header">
                        <div>
                            <h4 className="dashboard-title d-flex align-items-center mb-2">
                                Dashboard 

                            </h4>
                            <div className="round-info">
                                <span className="round-number">Round #{roundNumber || 0}</span>
                                <span className="phase-badge" style={{ backgroundColor: getPhaseColor() }}>
                                    {(phase || "waiting").toUpperCase()}
                                </span>
                                {/* Removed current Round Winner badge as requested */}
                            </div>
                        </div>
                        <div className="game-timer">{timerDisplay || "00:00"}</div>
                    </div>

                    {/* Settings and Last 10 Winners Row */}
                    <Row>
                        <Col md={6}>
                            {/* Settings Card */}
                            <Card className="settings-card">
                                <CardHeader>
                                    <h5 className="mb-0">Settings</h5>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={12}>
                                            <div className="settings-row">
                                                <Label className="settings-label">Add Jackpot:</Label>
                                                <div className="multiplier-btn-group">
                                                    {multiplierOptions.map((mult) => (
                                                        <Button
                                                            key={mult}
                                                            className={`multiplier-btn ${currentMultiplier === mult ? 'active' : ''}`}
                                                            onClick={() => handleMultiplierChange(mult)}
                                                        >
                                                            {mult}x
                                                        </Button>
                                                    ))}

                                                    {/* Show custom multiplier button if it's a custom value */}
                                                    {isCustomMultiplier && (
                                                        <Button
                                                            className="multiplier-btn active"
                                                            onClick={() => setShowCustomModal(true)}
                                                        >
                                                            {currentMultiplier}x
                                                        </Button>
                                                    )}

                                                    {/* Custom button to add new custom value */}
                                                    <Button
                                                        className="multiplier-btn custom-btn"
                                                        onClick={() => setShowCustomModal(true)}
                                                    >
                                                        + Custom
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={6}>
                            {/* Recent Winning Numbers Card */}
                            <Card className="settings-card">
                                <CardHeader>
                                    <h5 className="mb-0">
                                        <i className="ri-trophy-line me-2"></i>
                                        Recent Winning Numbers
                                    </h5>
                                </CardHeader>
                                <CardBody>
                                    <div className="winners-grid">
                                        {lastTenWinners.length > 0 ? (
                                            <>
                                                {lastTenWinners.map((winner, index) => {
                                                    const displayWinner = typeof winner === 'object' && winner !== null 
                                                        ? (winner.winningSymbol !== undefined ? winner.winningSymbol : (winner.winningNumber !== undefined ? winner.winningNumber : ''))
                                                        : winner;
                                                    return (
                                                        <div key={index} className={`winner-number-badge ${getNumberColor(String(displayWinner))}`}>
                                                            {displayWinner}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <div className="text-muted text-center" style={{ padding: '10px 0' }}>
                                                <i className="ri-inbox-line me-2"></i>
                                                No winning numbers recorded yet
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Result Prediction Card */}
                    <Card className="prediction-card">
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Result Prediction</h5>
                            <span className="total-amount">
                                Total Point: {(Number(totalAmount) || 0).toLocaleString()}
                            </span>
                        </CardHeader>
                        <CardBody>
                            {renderRouletteTable(false)}
                        </CardBody>
                    </Card>

                    {/* Declare Result Manually Card */}
                    <Card className={`manual-winner-card ${!isManualWinnerWindowOpen ? 'disabled-card' : ''}`}>
                        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">
                                Declare Result Manually
                                {!isManualWinnerWindowOpen && (
                                    <span className="ms-2 badge bg-light text-dark">
                                        <i className="mdi mdi-lock me-1"></i>Locked
                                    </span>
                                )}
                            </h5>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                {manualCalc && activeManualNumber !== null && activeManualNumber !== undefined && (
                                    <div className="manual-calc-display">
                                        <span className="calc-label">Number: <strong>{activeManualNumber}</strong></span>
                                        <span className="calc-divider">|</span>
                                        <span className="calc-label">Payout: <strong className="text-warning">{manualCalc.payout.toLocaleString()}</strong></span>
                                        <span className="calc-divider">|</span>
                                        <span className="calc-label">Admin P&L: <strong className={manualCalc.adminPL >= 0 ? 'text-success' : 'text-danger'}>{manualCalc.adminPL >= 0 ? '+' : ''}{manualCalc.adminPL.toLocaleString()}</strong></span>
                                    </div>
                                )}
                                {isManualWinnerWindowOpen ? (
                                    <span className="manual-window-badge">Window Open</span>
                                ) : (
                                    <span className="manual-window-badge closed">Window Closed</span>
                                )}
                            </div>
                        </CardHeader>
                        <CardBody>
                            <div className="manual-board-wrap">
                                {renderRouletteTable(true)}
                                {!isManualWinnerWindowOpen && (
                                    <div className="manual-lock-overlay">
                                        <i className="ri-lock-2-line"></i>
                                        <span>Locked</span>
                                    </div>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>


            {/* Custom Multiplier Modal */}
            <Modal isOpen={showCustomModal} toggle={() => setShowCustomModal(false)}>
                <ModalHeader toggle={() => setShowCustomModal(false)}>
                    Set Custom Multiplier
                </ModalHeader>
                <ModalBody>
                    <div className="custom-multiplier-input">
                        <Label>Enter Multiplier Value:</Label>
                        <Input
                            type="number"
                            value={customMultiplier}
                            onChange={(e) => setCustomMultiplier(e.target.value)}
                            placeholder="e.g., 15"
                            min="1"
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowCustomModal(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCustomSubmit}>
                        Apply
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default Dashboard;
